import { useCallback } from 'react';
import { useImmerAtom } from 'jotai-immer';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { AllowedFileType, InputFileBlob } from '../../../../core/components/validation/ValidInputFile';
import utilSvc from '../../../../core/utilSvc';
import validSvc from '../../../../core/validSvc';

import { payorImportStateAtom } from '../payor_list_atoms';
import { PayorImportHook } from '../payor_list_types';
import { payorImportStateVM } from '../payor_list_view_models';
import { postImport } from '../../data/payorRepo';
import { HttpErrorUpsert, HttpImportResult } from '../../../../../global';
import { usePayorListStore } from '../pagehooks/usePayorListStore';

export const usePayorListImport = (): PayorImportHook => {
    const [state, setState] = useImmerAtom(payorImportStateAtom);
    const pageStore = usePayorListStore();

    const showImport = (): void => {
        setState(() => {
            const draft = payorImportStateVM();
            draft.showing = true;
            return draft;
        });
    };

    const cancel = useCallback((): void => {
        setState(() => payorImportStateVM());
    }, [setState]);

    const setFile = (prop: string, fileBlob: InputFileBlob | null): void => {
        setState(draft => {
            draft.ImportFile.file = fileBlob;
            draft.ImportFile.err = !!fileBlob ? '' : 'required';
        });
    };

    const modelValid = useCallback((): boolean => {
        let result = false;

        setState(draft => {
            const file = draft.ImportFile.file;
            const validSize = draft.ImportFile.size;
            const fileIsValid = !!file && file.size > validSize.min && file.size < validSize.max;
            if (!fileIsValid) {
                draft.ImportFile.err = 'required with size min ' + validSize.min + ' max ' + validSize.max;
                return draft;
            }

            const allowedTypes = draft.ImportFile.types;
            const fileExt =
                validSvc.isText(file.name) && file.name.lastIndexOf('.') >= 0
                    ? file.name.substring(file.name.lastIndexOf('.'))
                    : '';
            const fileExtValid = allowedTypes.indexOf(fileExt as AllowedFileType) > -1;
            draft.ImportFile.err = fileExtValid ? '' : 'must be one of ' + allowedTypes.join(', ');

            result = draft.ImportFile.err === '';
            return draft;
        });

        return result;
    }, [setState]);

    const submitAsync = useCallback(async (): Promise<void> => {
        setState(draft => {
            draft.importing = true;
            draft.confirming = false;
        });
        const r = await postImport(state.ImportFile.file as Blob);
        setState(draft => {
            draft.importing = false;
            draft.confirming = false;
        });

        const err = r as HttpErrorUpsert;
        if (utilSvc.http.isIgnore(err)) {
            setState(() => payorImportStateVM());
            ngDelegate.ok('Cannot Import', 'You do not have permission to import excluded payors.');
            return;
        }

        let errMsg = '';
        switch (r.status) {
            case 200:
                setState(() => payorImportStateVM());
                pageStore.getList();
                const ok = r as HttpImportResult;
                ngDelegate.ok('Payors Imported', 'Received: ' + ok.Received + ', Processed: ' + ok.Processed);
                break;
            case 409:
                errMsg = 'File upload issue, try again.';
                if (!!err.timeout) {
                    errMsg += ' (timeout)';
                }
                ngDelegate.ok('Cannot Import', errMsg);
                break;
            case 422:
                setState(() => payorImportStateVM());
                pageStore.getList();
                errMsg = validSvc.http422ImportResultToString(r);
                ngDelegate.ok('Cannot Import', errMsg);
                break;
            default:
                ngDelegate.ok('Cannot Import', 'Unknown error occurred.');
        }
    }, [state, setState, pageStore]);

    const submit = useCallback(
        (confirmed?: boolean): boolean => {
            if (state.importing) {
                return false;
            }

            if (!modelValid()) {
                ngDelegate.ok('Cannot Import', 'Check file selection');
                return false;
            }

            if (!confirmed) {
                setState(draft => {
                    draft.confirming = true;
                });
                return false;
            }
            submitAsync();
            return true;
        },
        [state.importing, modelValid, setState, submitAsync],
    );

    return {
        state,
        showImport,
        setFile,
        submit,
        cancel,
    };
};
